/**
 * ErrorBoundary component to catch React errors and display a user-friendly message
 */

import React from 'react';
import i18n from '../../../i18n';

// Simple inline styles to avoid dependency on styled-components and theme
const errorContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '20px',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  fontFamily: "'Montserrat', sans-serif",
};

const errorIconStyle = {
  fontSize: '64px',
  marginBottom: '20px',
  fontWeight: 'bold',
  color: '#c10a0a',
};

const errorTitleStyle = {
  fontSize: '24px',
  fontWeight: 600,
  marginBottom: '12px',
  color: '#202020',
  fontFamily: "'Montserrat', sans-serif",
};

const errorMessageStyle = {
  fontSize: '16px',
  marginBottom: '32px',
  color: '#747775',
  maxWidth: '500px',
  fontFamily: "'Montserrat', sans-serif",
};

const reloadButtonStyle = {
  width: 'auto',
  minWidth: '200px',
  height: '45px',
  backgroundColor: '#00A898',
  color: '#ffffff',
  border: 'none',
  borderRadius: '30px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: "'Montserrat', sans-serif",
  padding: '10px 28px',
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Store error details in state (optional, for future error reporting)
    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    // Reset error state and reload the page
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  getTranslation = (key) => {
    try {
      return i18n.t(key, { ns: 'common' });
    } catch (e) {
      // Fallback translations if i18n is not available
      const fallbacks = {
        'error.title': 'Something went wrong',
        'error.message': 'An unexpected error occurred. Please reload the page to continue.',
        'error.reload': 'Reload Page'
      };
      return fallbacks[key] || key;
    }
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI with inline styles
      return (
        <div style={errorContainerStyle}>
          <div style={errorIconStyle}><span>!</span></div>
          <h2 style={errorTitleStyle}>{this.getTranslation('error.title')}</h2>
          <p style={errorMessageStyle}>{this.getTranslation('error.message')}</p>
          <button style={reloadButtonStyle} onClick={this.handleReload}>
            {this.getTranslation('error.reload')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
